import { useEffect, RefObject } from "react"

/**
 * Custom Hook to detect clicks outside a referenced element.
 * @param ref - React reference object linked to the target DOM element.
 * @param onClickOutside - Function to invoke when a click outside the referenced element is detected.
 */
const useClickOutside = (ref: RefObject<HTMLElement | undefined | null>, onClickOutside: () => void): void => {
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if click was outside the ref element
      if (ref?.current && !ref.current.contains(event.target as Node)) {
        onClickOutside?.()
      }
    }

    // Bind the event listener to the document
    document.addEventListener("mousedown", handleClickOutside, {
      passive: true, // Improve performance for passive event listeners
      capture: true, // Event capturing phase
    })

    // Cleanup event listener on component unmount or dependency changes
    return () => {
      document.removeEventListener("mousedown", handleClickOutside, {
        capture: true, // Remove event listener from capturing phase
      })
    }
  }, [ref, onClickOutside]) // Only re-run effect if ref or onClickOutside changes
}

export default useClickOutside
